import React, { useState, useEffect } from 'react';
import { 
  FaHeart, 
  FaComment, 
  FaUserPlus, 
  FaThumbsUp, 
  FaEllipsisH, 
  FaCheck, 
  FaTimes,
  FaSpinner
} from 'react-icons/fa';
import AppBar from '../components/AppBar';
import { notificationsAPI } from '../services/api';
import toast from 'react-hot-toast';

const NotificationsScreen = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [markingAsRead, setMarkingAsRead] = useState(null);

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const response = await notificationsAPI.getNotifications();
      console.log('🔔 Notifications fetched:', response);
      setNotifications(response || []);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  const getNotificationTypeInfo = (type) => {
    switch (type) {
      case 1: // Like
        return { icon: FaHeart, color: 'text-red-500', label: 'liked your post' };
      case 2: // Comment
        return { icon: FaComment, color: 'text-blue-500', label: 'commented on your post' };
      case 3: // Follow
        return { icon: FaUserPlus, color: 'text-green-500', label: 'started following you' };
      case 4: // Message
        return { icon: FaComment, color: 'text-purple-500', label: 'sent you a message' };
      case 5: // Mention
        return { icon: FaThumbsUp, color: 'text-yellow-500', label: 'mentioned you' };
      case 6: // Share
        return { icon: FaThumbsUp, color: 'text-indigo-500', label: 'shared your post' };
      case 7: // Live
        return { icon: FaHeart, color: 'text-pink-500', label: 'went live' };
      case 8: // Story
        return { icon: FaHeart, color: 'text-orange-500', label: 'posted a story' };
      case 9: // Payment
        return { icon: FaThumbsUp, color: 'text-green-500', label: 'sent you money' };
      case 10: // Group
        return { icon: FaUserPlus, color: 'text-blue-500', label: 'added you to a group' };
      case 11: // Reaction
        return { icon: FaHeart, color: 'text-red-500', label: 'reacted to your post' };
      default:
        return { icon: FaThumbsUp, color: 'text-gray-500', label: 'interacted with your content' };
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      setMarkingAsRead(notificationId);
      await notificationsAPI.markAsRead(notificationId);
      
      setNotifications(prev => 
        prev.map(notification => 
          notification.id === notificationId 
            ? { ...notification, seen: true }
            : notification
        )
      );
      
      toast.success('Marked as read');
    } catch (error) {
      console.error('Error marking as read:', error);
      toast.error('Failed to mark as read');
    } finally {
      setMarkingAsRead(null);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationsAPI.markAllAsRead();
      
      setNotifications(prev => 
        prev.map(notification => ({ ...notification, seen: true }))
      );
      
      toast.success('All notifications marked as read');
    } catch (error) {
      console.error('Error marking all as read:', error);
      toast.error('Failed to mark all as read');
    }
  };

  const handleFollowBack = async (userId) => {
    // This would need to be implemented with the users API
    toast.success('Follow request sent!');
  };

  const filteredNotifications = notifications.filter(notification => {
    if (activeFilter === 'all') return true;
    if (activeFilter === 'unread') return !notification.seen;
    if (activeFilter === 'likes') return notification.type === 1 || notification.type === 11;
    if (activeFilter === 'comments') return notification.type === 2;
    if (activeFilter === 'follows') return notification.type === 3;
    return true;
  });

  const unreadCount = notifications.filter(n => !n.seen).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-weloop-dark">
        <AppBar />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex justify-center items-center py-20">
            <div className="flex items-center gap-3 text-gray-500">
              <FaSpinner className="animate-spin" />
              Loading notifications...
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-weloop-dark">
      <AppBar />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-6 pb-4 border-b border-weloop-gray-lighter">
          <h1 className="text-2xl sm:text-3xl font-bold text-white">
            Notifications
            {unreadCount > 0 && (
              <span className="ml-3 bg-weloop-green text-white text-sm px-2 py-1 rounded-full">
                {unreadCount}
              </span>
            )}
          </h1>
          
          {unreadCount > 0 && (
            <button
              onClick={handleMarkAllAsRead}
              className="text-weloop-green hover:text-weloop-green-dark text-sm font-medium transition-colors"
            >
              Mark all as read
            </button>
          )}
        </div>

        {/* Filters */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {[
            { key: 'all', label: 'All' },
            { key: 'unread', label: 'Unread' },
            { key: 'likes', label: 'Likes' },
            { key: 'comments', label: 'Comments' },
            { key: 'follows', label: 'Follows' }
          ].map(filter => (
            <button
              key={filter.key}
              onClick={() => setActiveFilter(filter.key)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                activeFilter === filter.key
                  ? 'bg-weloop-green text-white'
                  : 'bg-weloop-gray text-gray-300 hover:bg-weloop-gray-light'
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>

        {/* Notifications List */}
        {filteredNotifications.length === 0 ? (
          <div className="text-center py-20 text-gray-500">
            <div className="text-lg font-semibold text-white mb-2">
              No notifications
            </div>
            <div className="text-base text-gray-400">
              {activeFilter === 'all' 
                ? "You're all caught up!" 
                : `No ${activeFilter} notifications`
              }
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredNotifications.map((notification) => {
              const typeInfo = getNotificationTypeInfo(notification.type);
              const IconComponent = typeInfo.icon;
              
              return (
                <div
                  key={notification.id}
                  className={`bg-weloop-gray/20 rounded-xl p-4 transition-all duration-200 hover:-translate-y-1 hover:shadow-lg cursor-pointer ${
                    !notification.seen ? 'border-l-4 border-weloop-green bg-weloop-gray/30' : ''
                  }`}
                >
                  <div className="flex items-start gap-3">
                    {/* Avatar */}
                    <img
                      src={notification.user_from?.meta?.profile_image || '/default-avatar.png'}
                      alt={notification.user_from?.meta?.name || notification.user_from?.username}
                      className="w-12 h-12 rounded-full object-cover flex-shrink-0"
                    />
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-white font-semibold text-sm">
                          {notification.user_from?.meta?.name || notification.user_from?.username}
                        </span>
                        <IconComponent className={`w-4 h-4 ${typeInfo.color}`} />
                        <span className="text-gray-400 text-sm">
                          {typeInfo.label}
                        </span>
                      </div>
                      
                      <div className="text-gray-500 text-xs mb-2">
                        {notification.timeago}
                      </div>
                      
                      {/* Reaction emoji if exists */}
                      {notification.reaction && (
                        <div className="text-2xl mb-2">
                          {notification.reaction}
                        </div>
                      )}
                    </div>
                    
                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      {!notification.seen && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMarkAsRead(notification.id);
                          }}
                          disabled={markingAsRead === notification.id}
                          className="p-1 text-gray-500 hover:text-weloop-green transition-colors disabled:opacity-50"
                        >
                          {markingAsRead === notification.id ? (
                            <FaSpinner className="w-3 h-3 animate-spin" />
                          ) : (
                            <FaCheck className="w-3 h-3" />
                          )}
                        </button>
                      )}
                      
                      {notification.type === 3 && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleFollowBack(notification.user_from_id);
                          }}
                          className="px-3 py-1 bg-weloop-green hover:bg-weloop-green-dark text-white text-xs rounded-full transition-colors"
                        >
                          Follow back
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationsScreen; 